import React, { useEffect, useState } from "react";
import moment from "moment";
import { Typography, Tabs, Table, Button, Spin, Pagination, Select, message, Checkbox, Row, Col, Modal, Form, Input } from "antd";
import SidebarLayout from "../BankerUI/SidebarLayout";
import _BankAdminService from "../../modules/BankAdminService";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ButtonComponent from "../../components/Button/Button";
import FormItemSelect from "../../components/FormItems/FormItemSelect";
import "./AdvancesAndRepayments.scss";
import { SENIOR_PRIVATE_BANKER_ROLE_ID } from "../../constants/DataConstants";

const { TabPane } = Tabs;
const { Title } = Typography;
const { Option } = Select;

const AdvancesAndRepaymentsForBanker = () => {
  const loginEmail = localStorage.getItem("loginEmail");
  // const bankAdminId = localStorage.getItem("BankAdminId");
  const BankerId = localStorage.getItem("BankerId");
  const roleId = Number(localStorage.getItem("RoleId") ?? 0);

  const [selectedSponsor, setSelectedSponsor] = useState(null);
  const [selectedSponsorDetails, setSelectedSponsorDetails] = useState(null);
  const [firmList, setFirmList] = useState([]);
  const [selectedFirm, setSelectedFirm] = useState(null);
  const [loanProgramTypeList, setLoanProgramTypeList] = useState([]);
  const [selectedLoanProgramType, setSelectedLoanProgramType] = useState(null);
  const [drawsData, setDrawsData] = useState([]);
  const [repaymentsData, setRepaymentsData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Right-of-"Choose Credit Facility" last-submitted info, shown per active tab.
  const [activeTabKey, setActiveTabKey] = useState("1");

  // Submitted Batches Awaiting Certification section has its own Advances/Repayments tabs,
  // independent of the lower Advances/Repayments table's activeTabKey.
  const [certifyTabKey, setCertifyTabKey] = useState("1");
  const [lastSubmittedByNameAdvance, setLastSubmittedByNameAdvance] = useState(null);
  const [lastSubmittedDateAdvance, setLastSubmittedDateAdvance] = useState(null);
  const [lastSubmittedByNameRepayment, setLastSubmittedByNameRepayment] = useState(null);
  const [lastSubmittedDateRepayment, setLastSubmittedDateRepayment] = useState(null);

  // Certify & Submit Batch popup -- the attestation (Name + Date/Time) step before certifying
  // whichever Submitted batch(es) are selected below.
  const [certifyModalVisible, setCertifyModalVisible] = useState(false);
  const [certifyBatchType, setCertifyBatchType] = useState(null); // "Advance" | "Repayment"
  const [submittedByName, setSubmittedByName] = useState("");
  const [submittedDate, setSubmittedDate] = useState(null);
  const [certifyForm] = Form.useForm();

  // Submitted batches awaiting certification (CertifyBatch.md Section 5.4) -- grouped by
  // BatchNumber, across every facility this Banker is associated with, independent of the
  // Firm/Credit Facility selectors above (which only scope the raw applications table).
  const [submittedAdvanceBatches, setSubmittedAdvanceBatches] = useState([]);
  const [submittedRepaymentBatches, setSubmittedRepaymentBatches] = useState([]);
  const [selectedAdvanceBatchNumbers, setSelectedAdvanceBatchNumbers] = useState([]);
  const [selectedRepaymentBatchNumbers, setSelectedRepaymentBatchNumbers] = useState([]);
  const [certifyingBatch, setCertifyingBatch] = useState(false);

  // pagination states
  const [drawsPage, setDrawsPage] = useState(1);
  const [repaymentsPage, setRepaymentsPage] = useState(1);
  const pageSize = 5;

  const bankadminIdRaw = localStorage.getItem("BankAdminId");
  const BankAdminId = bankadminIdRaw ? parseInt(bankadminIdRaw) : null;
  const [BankAdminID, setBankAdminID] = useState(BankAdminId);
  const [loanProgrameType, setLoanProgrameType] = useState();
  const [totalAvailability, setTotalAvailability] = useState();
  const [totalBorrowingBase, setTotalBorrowingBase] = useState();
  const [totalOutstanding, setTotalOutstanding] = useState();
  const [loanProgramSummary, setLoanProgramSummary] = useState({});

  const bankerRoleId = Number(localStorage.getItem("RoleId") ?? 0);
  const isBanker = bankerRoleId === SENIOR_PRIVATE_BANKER_ROLE_ID;

  // const getTableEmptyText = () => {
  //   const messageText = loanProgrameType === 2
  //     ? "This is a Subscription Line Facility."
  //     : "No Data";

  //   return <div className="table-empty-message">{messageText}</div>;
  // };

  const getTableEmptyText = () => <div className="table-empty-message">No Data.</div>;

  // Subscription Line of Credit (2) / Corporate Loan (4) facility rows -- Sponser.AdvanceRate is stored
  // as a plain number string (e.g. "92"), unlike the 1/3 tables' AdvanceRate which the backend already
  // suffixes with "%".
  const formatPercent = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const str = String(value);
    return str.includes("%") ? str : `${str}%`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    const cleanValue = typeof value === "string" ? value.replace(/[^0-9.-]+/g, "") : value;
    const num = Number(cleanValue);
    return !isNaN(num) ? `$${num.toLocaleString("en-US", { minimumFractionDigits: 2 })}` : "-";
  };

  const formatDate = (value) => {
    if (!value) return "-";
    const d = new Date(value);
    return isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-US");
  };

  useEffect(() => {
    if (BankAdminID && BankerId) {
      fetchFirmList(BankerId, BankAdminID);
    }
  }, [BankAdminID, BankerId]);

  // Submitted batches awaiting certification -- fetched once for this Banker, independent of
  // the Firm/Credit Facility selectors, since it's a work queue across every facility they're
  // associated with (CertifyBatch.md Section 5.4).
  const fetchSubmittedBatches = async (batchType) => {
    try {
      const res = await _BankAdminService.getSubmittedBatchesForBanker(Number(BankerId), roleId, isBanker, batchType);
      const rows = res?.data?.Obj || [];
      const groupedByBatchNumber = {};
      rows.forEach((row) => {
        const key = row.BatchNumber;
        if (!groupedByBatchNumber[key]) {
          groupedByBatchNumber[key] = {
            BatchNumber: row.BatchNumber,
            SubmittedByName: row.SubmittedByName,
            SubmittedDate: row.SubmittedDate,
            FacilityName: row.Institution || row.PortfolioCompany || row.ClientName || "-",
            TotalAmount: 0,
            Count: 0,
          };
        }
        const rawAmount = batchType === "Advance" ? row.AdvanceAmount : row.RepaymentAmount;
        const amount = Number(String(rawAmount || "0").replace(/[^0-9.-]+/g, "")) || 0;
        groupedByBatchNumber[key].TotalAmount += amount;
        groupedByBatchNumber[key].Count += 1;
      });
      const grouped = Object.values(groupedByBatchNumber);
      if (batchType === "Advance") {
        setSubmittedAdvanceBatches(grouped);
      } else {
        setSubmittedRepaymentBatches(grouped);
      }
    } catch (error) {
      console.error("fetchSubmittedBatches error:", error);
    }
  };

  useEffect(() => {
    if (BankerId) {
      fetchSubmittedBatches("Advance");
      fetchSubmittedBatches("Repayment");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [BankerId]);
  useEffect(() => {
    const handleBankAdminIdChange = (e) => {
      const newId = e.detail.newId;
      setBankAdminID(newId);
    };

    window.addEventListener("BankAdminIdChanged", handleBankAdminIdChange);

    return () => {
      window.removeEventListener("BankAdminIdChanged", handleBankAdminIdChange);
    };
  }, []);
  // useEffect(() => {
  //   if (sponsors.length > 0) {
  //     const firstSponsor = sponsors[0];

  //     setSelectedSponsor(firstSponsor.SponserId);
  //     setSelectedSponsorDetails(firstSponsor);
  //     setLoanProgrameType(firstSponsor.LoanProgrameTypeId);
  //     console.log(firstSponsor, "firstSponsor")

  //     fetchFirmAdminData(firstSponsor.SponserId);
  //   }
  // }, [sponsors]);

  // 📌 Fetch firm list (Choose Credit Facility -> Select Firm)
  const fetchFirmList = async (BankerId, bankAdminId) => {
    try {
      setLoading(true);
      const res = await _BankAdminService.getFirmDropdownListByBankerIdV1(BankerId, roleId, isBanker);

      if (res?.data?.IsSuccess && res.data.Obj?.length > 0) {
        setFirmList(res.data.Obj);
        // default select first firm and cascade into its loan programe types
        const firstFirm = res.data.Obj[0];
        setSelectedFirm(firstFirm.Id);
        await fetchLoanProgramTypeList(firstFirm.Id);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching firm list:", error);
      message.error("Failed to load firms.");
    } finally {
      setLoading(false);
    }
  };

  // 📌 Fetch loan programe type list for the selected firm (Select Credit Facility)
  const fetchLoanProgramTypeList = async (firmId, autoSelect = true) => {
    try {
      setLoading(true);
      const res = await _BankAdminService.getLoanProgrameTypeListByFirmId(firmId, { bankerId: BankerId, roleId, isBanker });

      if (res?.data?.IsSuccess && res.data.Obj?.length > 0) {
        setLoanProgramTypeList(res.data.Obj);
        if (autoSelect) {
          // Each entry is keyed by its own unique SponserId (a firm can have multiple facilities
          // sharing the same LoanProgrameTypeId, differentiated only by Credit Facility Name), so the
          // Select is bound to SponserId while LoanProgrameTypeId is kept separately for the
          // type-branching (2/4 vs others) fetchFirmAdminData already does.
          const firstType = res.data.Obj[0];
          setSelectedLoanProgramType(firstType.SponserId);
          setLoanProgrameType(firstType.LoanProgrameTypeId);
          await fetchFirmAdminData(firmId, firstType.LoanProgrameTypeId, firstType.SponserId);
        } else {
          setSelectedLoanProgramType(null);
          setLoanProgrameType(null);
          setDrawsData([]);
          setRepaymentsData([]);
        }
      } else {
        setLoanProgramTypeList([]);
        setSelectedLoanProgramType(null);
        setLoanProgrameType(null);
        setDrawsData([]);
        setRepaymentsData([]);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching loan programe type list:", error);
      message.error("Failed to load loan programe types.");
    } finally {
      setLoading(false);
    }
  };

  // // 📌 Fetch data by sponsorId
  // const fetchFirmAdminData = async (sponsorId) => {
  //   try {
  //     setLoading(true);
  //     const res =
  //       await _BankAdminService.getFirmAdminAdvanceRepaymentDataBySponserId(
  //         sponsorId
  //       );

  //     if (res?.data?.IsSuccess && res.data.Obj) {

  //       setTotalAvailability(res.data.Obj.TotalAvailability)
  //       setTotalBorrowingBase(res.data.Obj.TotalBorrowingBase)
  //       setTotalOutstanding(res.data.Obj.TotalOutstanding)
  //       if (loanProgrameType === 2) {
  //         console.log(loanProgrameType, "tyoe 2");
  //         setDrawsData(res.data.Obj.firmAdvanceSubscriptionModel || []);
  //         setRepaymentsData(res.data.Obj.firmAdvanceRePaymentSubscriptionModel || []);
  //       } else {
  //         console.log(loanProgrameType, "tyoe norm");
  //         setDrawsData(res.data.Obj.firmAdvanceDrawModel || []);
  //         setRepaymentsData(res.data.Obj.firmAdvanceRePaymentModels || []);
  //       }

  //     } else {
  //       setDrawsData([]);
  //       setRepaymentsData([]);
  //       message.warning("No data available for this sponsor.");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching advance/repayment data:", error);
  //     message.error("Failed to load advance/repayment data.");
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const fetchFirmAdminData = async (firmId, programType, sponserId) => {
    try {
      setLoading(true);

      const res =
        await _BankAdminService.getFirmAdminAdvanceRepaymentDataByFirmAndLoanProgrameType(
          firmId,
          programType,
          sponserId
        );

      if (res?.data?.IsSuccess && res.data.Obj) {
        const data = res.data.Obj;

        setTotalAvailability(data.TotalAvailability);
        setTotalBorrowingBase(data.TotalBorrowingBase);
        setTotalOutstanding(data.TotalOutstanding);

        setLoanProgramSummary({
          Name: data.Name,
          TotalProgrameSize: data.TotalProgrameSize,
          AdvanceRate: data.AdvanceRate,
          MaxLTV: data.MaxLTV,
          RepaymentBasis: data.RepaymentBasis,
          SweepPercentage: data.SweepPercentage,
          IntrestExpense: data.IntrestExpense,
        });

        setLastSubmittedByNameAdvance(data.LastSubmittedByNameAdvance || null);
        setLastSubmittedDateAdvance(data.LastSubmmitedDateAdvance || null);
        setLastSubmittedByNameRepayment(data.LastSubmittedByNameRepayment || null);
        setLastSubmittedDateRepayment(data.LastSubmmitedDateRepayment || null);

        // ✅ FIX: use programType (NOT state)
        if (Number(programType) === 2) {
          setDrawsData(data.firmAdvanceSubscriptionModel || []);
          setRepaymentsData(
            data.firmAdvanceRePaymentSubscriptionModel || []
          );
        } else if (Number(programType) === 4) {
          setDrawsData(data.firmAdvanceCorporateLoanModel || []);
          setRepaymentsData(data.firmAdvanceRePaymentCorporateLoanModel || []);
        } else {
          setDrawsData(data.firmAdvanceDrawModel || []);
          setRepaymentsData(data.firmAdvanceRePaymentModels || []);
        }
      } else {
        setDrawsData([]);
        setRepaymentsData([]);
        setLastSubmittedByNameAdvance(null);
        setLastSubmittedDateAdvance(null);
        setLastSubmittedByNameRepayment(null);
        setLastSubmittedDateRepayment(null);
        message.warning("No data available for this credit facility.");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Failed to load data.");
    } finally {
      setLoading(false);
    }
  };

  const handleFirmChange = (firmId) => {
    setSelectedFirm(firmId);
    setSelectedLoanProgramType(null);
    setLoanProgramTypeList([]);
    setDrawsData([]);
    setRepaymentsData([]);
    setSelectedSponsor(null);
    fetchLoanProgramTypeList(firmId, false);
  };

  const handleLoanProgramTypeChange = (value) => {
    // `value` is the selected dropdown entry's SponserId (unique per row); look up its
    // LoanProgrameTypeId from the list to keep the type-branching in fetchFirmAdminData working.
    const matchedFacility = loanProgramTypeList.find((type) => type.SponserId === value);
    setSelectedLoanProgramType(value);
    setLoanProgrameType(matchedFacility?.LoanProgrameTypeId ?? null);
    fetchFirmAdminData(selectedFirm, matchedFacility?.LoanProgrameTypeId, value);
  };

  // // 📌 Function to download Draws PDF
  // const handleDownloadDrawsPDF = () => {
  //   const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   const leftMargin = 3;
  //   const rightMargin = 3;
  //   doc.text("Advances Report", 20, 30);
  //   doc.setLineWidth(0.3);
  //   doc.line(leftMargin, 40, pageWidth - rightMargin, 40);
  //   autoTable(doc, {
  //     startY: 60,
  //     margin: { left: 3, right: 3 },
  //     headStyles: {
  //       fillColor: [25, 58, 106],
  //       textColor: [255, 255, 255],
  //       fontStyle: 'normal',
  //       cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
  //       lineWidth: 0.3,
  //       lineColor: [0, 0, 0],
  //     },
  //     bodyStyles: {
  //       cellPadding: 6,
  //       lineWidth: 0.3,
  //       lineColor: [0, 0, 0],
  //     },
  //     head: [
  //       [
  //         "Client Name",
  //         "Account Number",
  //         "Loan Amount ",
  //         "Outstanding ",
  //         "Availability",
  //         "Fair Market Value ",
  //         "Current LTV",
  //         "Gross Capital Call ",
  //         "Advance Rate ",
  //         "Maximum Available Draw",
  //         "Post-Funding Availability",
  //         "Post-Funding LTV",
  //         "LTV Result",
  //       ],
  //     ],
  //     body: drawsData.map((item) => [
  //       item.BorrowerName,
  //       item.AccountNumber,
  //       item.LoanAmount || "-",
  //       item.OutstandingBalanace || "-",
  //       item.Availability || "-",
  //       item.FairMarketValue || "-",
  //       item.AdvanceAmount || "-",
  //       item.CurrentLTV || "-",
  //       item.GrossDraw || "-",
  //       item.AdvanceRate || "-",
  //       item.MaximumAvailableDraw || "-",
  //       item.PostFundingAvailability || "-",
  //       item.PostFundingLTV || "-",
  //       item.LTVResult || "-",
  //     ]),
  //   });

  //   doc.save("Advances.pdf");
  // };

  const handleDownloadDrawsPDF = () => {
    let head = [];
    let body = [];

    // SWITCH BASED ON LOAN TYPE
    if (loanProgrameType === 2) {
      // SUBSCRIPTION LINE OF CREDIT -- one row per Sponser facility
      head = [[
        "Borrower Fund",
        "Institution",
        "Total Commitment",
        "Unfunded Commitment",
        "Borrowing Base",
        "Outstanding",
        "Availability",
        "Advance Rate",
        "Requested Draw",
        "Post-Draw Availability",
        "Validation Result",
      ]];

      body = drawsData.map((item) => [
        item.BorrowerFund || "-",
        item.Institution || "-",
        item.TotalCommitment || "-",
        item.UnfundedCommitment || "-",
        item.BorrowingBase || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        item.AdvanceRate || "-",
        item.RequestedDraw || "-",
        item.PostDrawAvailability || "-",
        item.ValidationResult || "-",
      ]);

    } else if (loanProgrameType === 4) {
      // CORPORATE LOAN -- one row per Sponser facility (one portfolio company each)
      head = [[
        "Portfolio Company",
        "Institution",
        "Loan Structure",
        "Original Facility Size",
        "Current Outstanding Balance",
        "Availability",
        "Maturity Date",
        "Requested Draw",
        "Post-Draw Availability",
        "Validation Result",
      ]];

      body = drawsData.map((item) => [
        item.PortfolioCompany || "-",
        item.Institution || "-",
        item.LoanStructure || "-",
        item.OriginalFacilitySize || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        formatDate(item.MaturityDate),
        item.RequestedDraw || "-",
        item.PostDrawAvailability || "-",
        item.ValidationResult || "-",
      ]);

    } else {
      // NORMAL LOAN
      head = [[
        "Client Name",
        "Facility Reference",
        "Loan Amount",
        "Outstanding",
        "Availability",
        "Fair Market Value",
        "Current LTV",
        "Gross Capital Call",
        "Advance Rate",
        "Max LTV",
        "Advance Amount",
        "Post Funding Outstanding",
        "Maximum Available Draw",
        "Post-Funding Availability",
        "Post-Funding LTV",
        "LTV Result",
        "Fund(s)",
      ]];

      body = drawsData.map((item) => [
        item.BorrowerName,
        item.AccountNumber,
        item.LoanAmount || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        item.FairMarketValue || "-",
        item.CurrentLTV || "-",
        item.GrossDraw || "-",
        item.AdvanceRate || "-",
        item.MaxLTV || "-",
        item.AdvanceAmount || "-",
        item.PostFundingOutstanding || "-",
        item.MaximumAvailableDraw || "-",
        item.PostFundingAvailability || "-",
        item.PostFundingLTV || "-",
        item.LTVResult || "-",
        item.FundPledges || "-",
      ]);
    }

    const pageWidth = Math.max(842, head[0].length * 110);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: [pageWidth, 595] });

    const leftMargin = 3;
    const rightMargin = 3;

    doc.text("Advance & Repayment — Loan Program", 10, 30);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, 40, pageWidth - rightMargin, 40);

    // The "Loan Program" summary sub-table only applies to 1/3 -- Subscription Line of Credit (2) /
    // Corporate Loan (4) facilities don't have those fields (loanProgramSummary is left unset for
    // them), so it's skipped and the main table starts right under the title line instead.
    let tableStartY = 50;
    if (loanProgrameType !== 2 && loanProgrameType !== 4) {
      const summaryColumnCount = 7;
      const summaryTableWidth = pageWidth - leftMargin - rightMargin;

      doc.text("Loan Program", 10, 68);

      autoTable(doc, {
        startY: 72,
        margin: { left: 3, right: 3 },
        tableWidth: summaryTableWidth,
        head: [[
          "Loan Program",
          "Program Size",
          "Advance Rate",
          "Max LTV",
          "Interest Rate",
          "Repayment Basis",
          "Sweep %"
        ]],
        body: [[
          loanProgramSummary.Name || "-",
          loanProgramSummary.TotalProgrameSize || "-",
          loanProgramSummary.AdvanceRate || "-",
          loanProgramSummary.MaxLTV || "-",
          loanProgramSummary.IntrestExpense || "-",
          loanProgramSummary.RepaymentBasis || "-",
          loanProgramSummary.SweepPercentage || "-",
        ]],

        styles: {
          cellWidth: summaryTableWidth / summaryColumnCount,
        },

        headStyles: {
          fillColor: [25, 58, 106],
          textColor: [255, 255, 255],
          fontStyle: 'normal',
          fontSize: 18,
          cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
          lineWidth: 0.3,
          lineColor: [0, 0, 0],
        },

        bodyStyles: {
          fontSize: 18,
          cellPadding: 6,
          lineWidth: 0.3,
          lineColor: [0, 0, 0],
        },
      });

      tableStartY = doc.lastAutoTable.finalY + 40;
    }

    doc.text("Advance", 10, tableStartY - 5);

    autoTable(doc, {
      startY: tableStartY,
      margin: { left: 3, right: 3 },
      head,
      body,
      headStyles: {
        fillColor: [25, 58, 106],
        textColor: [255, 255, 255],
        fontStyle: 'normal',
        fontSize: 18,
        cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        fontSize: 18,
        cellPadding: 6,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
    });

    doc.save("Advances.pdf");
  };

  // 📌 Function to download Repayments PDF
  // const handleDownloadRepaymentsPDF = () => {
  //   const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  //   const pageWidth = doc.internal.pageSize.getWidth();
  //   doc.text("Repayments Report", 20, 30);
  //   doc.setLineWidth(0.3);
  //   const leftMargin = 3;
  //   const rightMargin = 3;
  //   doc.setLineWidth(0.5);
  //   doc.line(leftMargin, 40, pageWidth - rightMargin, 40);
  //   autoTable(doc, {
  //     startY: 60,
  //     margin: { left: 3, right: 3 },
  //     headStyles: {
  //       fillColor: [25, 58, 106],
  //       textColor: [255, 255, 255],
  //       fontStyle: 'normal',
  //       cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
  //       lineWidth: 0.3,
  //       lineColor: [0, 0, 0],
  //     },
  //     bodyStyles: {
  //       cellPadding: 6,
  //       lineWidth: 0.3,
  //       lineColor: [0, 0, 0],
  //     },
  //     head: [
  //       [
  //         "Client Name",
  //         "Account Number",
  //         "Loan Amount ",
  //         "Outstanding ",
  //         "Availability",
  //         "Fair Market Value",
  //         "Cost Basis",
  //         "Gains",
  //         "Tax Rate",
  //         "Repayment",
  //         "Net Repayment",
  //         "Post-Repayment Outstanding",
  //         "Post-Repayment LTV",
  //       ],
  //     ],
  //     body: repaymentsData.map((item) => [
  //       item.BorrowerName,
  //       item.AccountNumber,
  //       item.LoanAmount || "-",
  //       item.OutstandingBalanace || "-",
  //       item.Availability,
  //       item.FairMarketValue,
  //       item.CostBasis,
  //       item.Gains,
  //       item.TaxRate || "-",
  //       item.RePayment || "-",
  //       item.NetPayment || "-",
  //       item.PostRepaymentOutstanding || "-",
  //       item.PostRepaymentLTV || "-",
  //     ]),
  //   });

  //   doc.save("Repayments.pdf");
  // };

  const handleDownloadRepaymentsPDF = () => {
    let head = [];
    let body = [];

    // SWITCH BASED ON LOAN TYPE
    if (loanProgrameType === 2) {
      // SUBSCRIPTION LINE OF CREDIT -- one row per Sponser facility
      head = [[
        "Borrower Fund",
        "Institution",
        "Total Commitment",
        "Unfunded Commitment",
        "Borrowing Base",
        "Outstanding",
        "Availability",
        "Advance Rate",
        "Repayment",
        "Post-Repayment Availability",
        "Validation Result",
      ]];

      body = repaymentsData.map((item) => [
        item.BorrowerFund || "-",
        item.Institution || "-",
        item.TotalCommitment || "-",
        item.UnfundedCommitment || "-",
        item.BorrowingBase || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        item.AdvanceRate || "-",
        item.Repayment || "-",
        item.PostRepaymentAvailability || "-",
        item.ValidationResult || "-",
      ]);

    } else if (loanProgrameType === 4) {
      // CORPORATE LOAN -- one row per Sponser facility (one portfolio company each)
      head = [[
        "Portfolio Company",
        "Institution",
        "Loan Structure",
        "Original Facility Size",
        "Current Outstanding Balance",
        "Availability",
        "Maturity Date",
        "Repayment Amount",
        "Post-Repayment Availability",
        "Validation Result",
      ]];

      body = repaymentsData.map((item) => [
        item.PortfolioCompany || "-",
        item.Institution || "-",
        item.LoanStructure || "-",
        item.OriginalFacilitySize || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        formatDate(item.MaturityDate),
        item.RepaymentAmount || "-",
        item.PostRepaymentAvailability || "-",
        item.ValidationResult || "-",
      ]);

    } else {
      // NORMAL LOAN
      head = [[
        "Client Name",
        "Facility Reference",
        "Loan Amount",
        "Outstanding",
        "Availability",
        "Gross RePayment",
        // "Fair Market Value",
        "Cost Basis",
        "Sweep%",
        "Tax Rate",
        "Gains",
        "Tax",
        "Post-Repayment Availability",
        "Repayment Basis Amount",
        "Repayment Amount",
        "Post-Repayment Outstanding",
        "Post-Repayment LTV",
        "Repayment",
        "Net Repayment",
        "Fund(s)",
      ]];

      body = repaymentsData.map((item) => [
        item.BorrowerName,
        item.AccountNumber,
        item.LoanAmount || "-",
        item.OutstandingBalanace || "-",
        item.Availability || "-",
        item.GrossRePayment || "-",
        // item.FairMarketValue || "-",
        item.CostBasis || "-",
        item.SweepPercentage || "-",
        item.TaxRate || "-",
        item.Gains || "-",
        item.Tax || "-",
        item.PostRepaymentAvailability || "-",
        item.RepaymentBasisAmount || "-",
        item.RepaymentAmount || "-",
        item.PostRepaymentOutstanding || "-",
        item.PostRepaymentLTV || "-",
        item.RePayment || "-",
        item.NetPayment || "-",
        item.FundPledges || "-",
      ]);
    }

    const pageWidth = Math.max(842, head[0].length * 110);
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: [pageWidth, 595] });

    const leftMargin = 3;
    const rightMargin = 3;

    doc.text("Advance & Repayment — Loan Program", 10, 30);
    doc.setLineWidth(0.3);
    doc.line(leftMargin, 40, pageWidth - rightMargin, 40);

    // See handleDownloadDrawsPDF for why this summary sub-table is skipped for 2/4.
    let tableStartY = 50;
    if (loanProgrameType !== 2 && loanProgrameType !== 4) {
      const summaryColumnCount = 7;
      const summaryTableWidth = pageWidth - leftMargin - rightMargin;

      doc.text("Loan Program", 10, 68);

      autoTable(doc, {
        startY: 72,
        margin: { left: 3, right: 3 },
        tableWidth: summaryTableWidth,
        head: [[
          "Loan Program",
          "Program Size",
          "Advance Rate",
          "Max LTV",
          "Interest Rate",
          "Repayment Basis",
          "Sweep %"
        ]],
        body: [[
          loanProgramSummary.Name || "-",
          loanProgramSummary.TotalProgrameSize || "-",
          loanProgramSummary.AdvanceRate || "-",
          loanProgramSummary.MaxLTV || "-",
          loanProgramSummary.IntrestExpense || "-",
          loanProgramSummary.RepaymentBasis || "-",
          loanProgramSummary.SweepPercentage || "-",
        ]],
        styles: {
          cellWidth: summaryTableWidth / summaryColumnCount,
        },
        headStyles: {
          fillColor: [25, 58, 106],
          textColor: [255, 255, 255],
          fontStyle: 'normal',
          fontSize: 18,
          cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
          lineWidth: 0.3,
          lineColor: [0, 0, 0],
        },
        bodyStyles: {
          fontSize: 18,
          cellPadding: 6,
          lineWidth: 0.3,
          lineColor: [0, 0, 0],
        },
      });

      tableStartY = doc.lastAutoTable.finalY + 40;
    }

    doc.text("Repayment", 10, tableStartY - 5);

    autoTable(doc, {
      startY: tableStartY,
      margin: { left: 3, right: 3 },
      head,
      body,
      headStyles: {
        fillColor: [25, 58, 106],
        textColor: [255, 255, 255],
        fontStyle: 'normal',
        fontSize: 18,
        cellPadding: { top: 10, bottom: 10, left: 4, right: 4 },
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
      bodyStyles: {
        fontSize: 18,
        cellPadding: 6,
        lineWidth: 0.3,
        lineColor: [0, 0, 0],
      },
    });

    doc.save("Repayments.pdf");
  };

  const openCertifyModal = (batchType) => {
    setCertifyBatchType(batchType);
    setSubmittedByName("");
    const now = new Date();
    setSubmittedDate(now.toISOString());
    certifyForm.setFieldsValue({
      submittedByName: "",
      submittedDateDisplay: now.toLocaleString(),
    });
    setCertifyModalVisible(true);
  };

  const closeCertifyModal = () => {
    setCertifyModalVisible(false);
    setCertifyBatchType(null);
    setSubmittedByName("");
    setSubmittedDate(null);
    certifyForm.resetFields();
  };

  const handleCertifyModalSubmit = async () => {
    try {
      await certifyForm.validateFields();
    } catch {
      return;
    }

    const batchType = certifyBatchType;
    const selectedBatchNumbers = batchType === "Advance" ? selectedAdvanceBatchNumbers : selectedRepaymentBatchNumbers;

    if (selectedBatchNumbers.length === 0) {
      message.warning("Please select at least one submitted batch to certify.");
      return;
    }

    closeCertifyModal();
    setCertifyingBatch(true);
    try {
      let anyFailed = false;
      for (const batchNumber of selectedBatchNumbers) {
        // eslint-disable-next-line no-await-in-loop
        const res = await _BankAdminService.certifyApplicantAdvanceRepaymentBatch(batchNumber, batchType, Number(BankerId));
        if (!res?.data?.IsSuccess) {
          anyFailed = true;
          message.error(res?.data?.Message || `Failed to certify batch ${batchNumber}.`);
        }
      }
      if (!anyFailed) {
        message.success("Batch(es) certified successfully.");
      }
      if (batchType === "Advance") {
        setSelectedAdvanceBatchNumbers([]);
      } else {
        setSelectedRepaymentBatchNumbers([]);
      }
      await fetchSubmittedBatches(batchType);

      // Certifying updates Outstanding/Availability for the certified facility (possibly the same
      // one currently shown below in the Draws/Repayments table) -- refresh it too so the Banker
      // sees the new values immediately instead of only after reselecting the facility or reloading.
      if (selectedFirm && selectedLoanProgramType) {
        await fetchFirmAdminData(selectedFirm, loanProgrameType, selectedLoanProgramType);
      }
    } catch (error) {
      console.error("Certify batch error:", error);
      message.error("Failed to certify the selected batch(es).");
    } finally {
      setCertifyingBatch(false);
    }
  };

  return (
    <SidebarLayout>
      <div className="advances-repayments-container">
        <Title level={3} className="page-title">
          Advances & Repayments
        </Title>
        <Title level={5} className="page-sub-title">
          Track borrower draws and repayments
        </Title>

        <div className="card-section" style={{ marginBottom: 16 }}>
          <Title level={5} className="form-title">Submitted Batches Awaiting Certification</Title>
          <Tabs
            activeKey={certifyTabKey}
            onChange={(key) => setCertifyTabKey(key)}
            type="line"
            className="tab-section"
          >
            <TabPane tab="Advances" key="1">
              <Table
                columns={[
                  { title: "Batch Number", dataIndex: "BatchNumber" },
                  { title: "Facility", dataIndex: "FacilityName" },
                  { title: "Submitted By", dataIndex: "SubmittedByName", render: (value) => value || "-" },
                  {
                    title: "Submitted Date",
                    dataIndex: "SubmittedDate",
                    render: (value) => (value ? moment.utc(value).local().format("MM/DD/YYYY hh:mm A") : "-"),
                  },
                  { title: "Applications", dataIndex: "Count" },
                  { title: "Total Advance Amount", dataIndex: "TotalAmount", render: (value) => formatCurrency(value) },
                  {
                    title: "Select",
                    render: (_, record) => (
                      <Checkbox
                        checked={selectedAdvanceBatchNumbers.includes(record.BatchNumber)}
                        onChange={(e) => {
                          setSelectedAdvanceBatchNumbers((prev) =>
                            e.target.checked
                              ? [...prev, record.BatchNumber]
                              : prev.filter((batchNumber) => batchNumber !== record.BatchNumber)
                          );
                        }}
                      />
                    ),
                  },
                ]}
                dataSource={submittedAdvanceBatches}
                rowKey="BatchNumber"
                pagination={false}
                className="transparent-table"
                locale={{ emptyText: getTableEmptyText() }}
                scroll={{ x: "max-content" }}
              />
              <div className="table-footer" style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <ButtonComponent
                  className="view-btn"
                  text="Certify & Submit Batch"
                  onClick={() => openCertifyModal("Advance")}
                  disabled={selectedAdvanceBatchNumbers.length === 0 || certifyingBatch}
                />
              </div>
            </TabPane>
            <TabPane tab="Repayments" key="2">
              <Table
                columns={[
                  { title: "Batch Number", dataIndex: "BatchNumber" },
                  { title: "Facility", dataIndex: "FacilityName" },
                  { title: "Submitted By", dataIndex: "SubmittedByName", render: (value) => value || "-" },
                  {
                    title: "Submitted Date",
                    dataIndex: "SubmittedDate",
                    render: (value) => (value ? moment.utc(value).local().format("MM/DD/YYYY hh:mm A") : "-"),
                  },
                  { title: "Applications", dataIndex: "Count" },
                  { title: "Total Repayment Amount", dataIndex: "TotalAmount", render: (value) => formatCurrency(value) },
                  {
                    title: "Select",
                    render: (_, record) => (
                      <Checkbox
                        checked={selectedRepaymentBatchNumbers.includes(record.BatchNumber)}
                        onChange={(e) => {
                          setSelectedRepaymentBatchNumbers((prev) =>
                            e.target.checked
                              ? [...prev, record.BatchNumber]
                              : prev.filter((batchNumber) => batchNumber !== record.BatchNumber)
                          );
                        }}
                      />
                    ),
                  },
                ]}
                dataSource={submittedRepaymentBatches}
                rowKey="BatchNumber"
                pagination={false}
                className="transparent-table"
                locale={{ emptyText: getTableEmptyText() }}
                scroll={{ x: "max-content" }}
              />
              <div className="table-footer" style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                <ButtonComponent
                  className="view-btn"
                  text="Certify & Submit Batch"
                  onClick={() => openCertifyModal("Repayment")}
                  disabled={selectedRepaymentBatchNumbers.length === 0 || certifyingBatch}
                />
              </div>
            </TabPane>
          </Tabs>
        </div>

        <div className="card-section choose-loan-program-section inputDisabledBlackColor">
          <div className="facility-selectors">
            <label className="choose-loan-program-label">Select Firm : </label>
            <Select
              placeholder="Select Firm"
              value={selectedFirm}
              onChange={handleFirmChange}
              popupClassName="loan-program-dropdown"

            >
              {firmList.map((firm) => (
                <Option key={firm.Id} value={firm.Id}>
                  {firm.FirmName}
                </Option>
              ))}
            </Select>

            <label className="choose-loan-program-label">Select Credit Facility : </label>
            <Select
              placeholder="Select Credit Facility"
              value={selectedLoanProgramType}
              onChange={handleLoanProgramTypeChange}
              popupClassName="loan-program-dropdown"
            >
              {loanProgramTypeList.map((type) => (
                <Option key={type.SponserId} value={type.SponserId}>
                  {type.Name}
                </Option>
              ))}
            </Select>
          </div>
          {(() => {
            const tabSubmittedByName = activeTabKey === "2" ? lastSubmittedByNameRepayment : lastSubmittedByNameAdvance;
            const tabSubmittedDate = activeTabKey === "2" ? lastSubmittedDateRepayment : lastSubmittedDateAdvance;
            return tabSubmittedByName && tabSubmittedDate ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, color: "#f4f4f5b2" }}>
                <span>
                  <strong style={{ color: "#f4f4f5" }}>Submitted By :</strong> {tabSubmittedByName}
                </span>
                <span>
                  <strong style={{ color: "#f4f4f5" }}>Date &amp; Time :</strong>{" "}
                  {moment.utc(tabSubmittedDate).local().format("MM/DD/YYYY hh:mm A")}
                </span>
              </div>
            ) : null;
          })()}
          {/* <FormItemSelect
              label=""
              name="sponsorSelect"
              placeholder="Select Sponsor"
              value={selectedSponsor}
              onChange={handleSponsorChange}
              options={sponsors.map((sponsor) => ({
                label: sponsor.SponserName,
                value: sponsor.SponserId,
              }))}
            /> */}
        </div>
        {/* Total Availability / Borrowing Base / Outstanding summary hidden per request
        <div className="card-section mb10">
          <Row gutter={16} className="loan-program-summary">
            <Col>
              <label className="choose-loan-program-label">Total Availability : </label>
              <label>{totalAvailability}</label>
            </Col>
            <Col>
              <label className="choose-loan-program-label">Total Borrowing Base : </label>
              <label>{totalBorrowingBase} </label>
            </Col>
            <Col>
              <label className="choose-loan-program-label">Total Outstanding : </label>
              <label>{totalOutstanding} </label>
            </Col>
          </Row>
        </div>
        */}

        {/* {loading ? (
          <Spin size="large" />
        ) : ( */}
        <Spin spinning={loading}>
          <Tabs onChange={(key) => setActiveTabKey(key)} defaultActiveKey="1" type="line" className="card-section tab-section">
            {/* ✅ Draws Tab */}
            <TabPane tab="Advances" key="1">
              {/* Subscription Line of Credit -- one row per Sponser facility */}
              {
                loanProgrameType === 2 &&
                <Table
                  columns={[
                    { title: "Borrower Fund", dataIndex: "BorrowerFund", render: (value) => value || "-" },
                    { title: "Facility", dataIndex: "Institution", render: (value) => value || "-" },
                    { title: "Total Commitment", dataIndex: "TotalCommitment", render: (value) => formatCurrency(value) },
                    { title: "Unfunded Commitment", dataIndex: "UnfundedCommitment", render: (value) => formatCurrency(value) },
                    { title: "Uncalled Capital", dataIndex: "UncalledCapital", render: (value) => formatCurrency(value) },
                    { title: "Borrowing Base", dataIndex: "BorrowingBase", render: (value) => formatCurrency(value) },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Outstanding </div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => formatCurrency(value),
                    },
                    { title: "Availability", dataIndex: "Availability", render: (value) => formatCurrency(value) },
                    { title: "Advance Rate", dataIndex: "AdvanceRate", render: (value) => formatPercent(value) },
                    { title: "Requested Draw", dataIndex: "RequestedDraw", render: (value) => formatCurrency(value) },
                    { title: "Post-Draw Availability", dataIndex: "PostDrawAvailability", render: (value) => formatCurrency(value) },
                    { title: "Validation Result", dataIndex: "ValidationResult", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={drawsData.slice((drawsPage - 1) * pageSize, drawsPage * pageSize)}
                  rowKey="SponserId"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }

              {/* Corporate Loan -- one row per Sponser facility (one portfolio company each) */}
              {
                loanProgrameType === 4 &&
                <Table
                  columns={[
                    { title: "Portfolio Company", dataIndex: "PortfolioCompany", render: (value) => value || "-" },
                    { title: "Facility", dataIndex: "Institution", render: (value) => value || "-" },
                    { title: "Loan Structure", dataIndex: "LoanStructure", render: (value) => value || "-" },
                    { title: "Original Facility Size", dataIndex: "OriginalFacilitySize", render: (value) => formatCurrency(value) },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Current Outstanding Balance</div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => formatCurrency(value),
                    },
                    { title: "Availability", dataIndex: "Availability", render: (value) => formatCurrency(value) },
                    { title: "Maturity Date", dataIndex: "MaturityDate", render: (value) => formatDate(value) },
                    { title: "Requested Draw", dataIndex: "RequestedDraw", render: (value) => formatCurrency(value) },
                    { title: "Post-Draw Availability", dataIndex: "PostDrawAvailability", render: (value) => formatCurrency(value) },
                    { title: "Validation Result", dataIndex: "ValidationResult", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={drawsData.slice((drawsPage - 1) * pageSize, drawsPage * pageSize)}
                  rowKey="SponserId"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }
              {/* for normal loan program */}
              {
                loanProgrameType !== 2 && loanProgrameType !== 4 &&
                <Table
                  columns={[
                    { title: "Client Name", dataIndex: "BorrowerName" },
                    { title: "Facility Reference", dataIndex: "AccountNumber" },
                    {
                      title: "Loan Amount ",
                      dataIndex: "LoanAmount",
                      render: (value) => value || "-"
                    },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Outstanding </div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => value || "-"
                    },
                    { title: "Availability", dataIndex: "Availability" },
                    { title: "Fair Market Value ", dataIndex: "FairMarketValue" },
                    { title: "Current LTV", dataIndex: "CurrentLTV" },
                    { title: "Gross Capital Call", dataIndex: "GrossDraw" },
                    { title: "Advance Rate", dataIndex: "AdvanceRate" },
                    { title: "Max LTV", dataIndex: "MaxLTV" },
                    { title: "Advance Amount", dataIndex: "AdvanceAmount", },
                    {
                      title: (
                        <div>
                          Maximum Available <br />
                          Draw
                        </div>
                      ),
                      dataIndex: "MaximumAvailableDraw",
                    },
                    { title: "Post Funding Outstanding", dataIndex: "PostFundingOutstanding" },
                    {
                      title: (
                        <div>
                          Post-Funding <br />
                          Availability
                        </div>
                      ),
                      dataIndex: "PostFundingAvailability",
                    },
                    {
                      title: (
                        <div>
                          Post-Funding <br />
                          LTV
                        </div>
                      ),
                      dataIndex: "PostFundingLTV",
                    },
                    { title: "LTV Result", dataIndex: "LTVResult" },
                    { title: "Fund(s)", dataIndex: "FundPledges", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={drawsData.slice((drawsPage - 1) * pageSize, drawsPage * pageSize)}
                  rowKey="BorrowerName"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }

              {/* <div className="table-footer">
               
                <ButtonComponent
                    block
                    className="view-btn"
                    text="Certify & Submit Batch"
                    onClick={handleDownloadDrawsPDF}
                  />
                <Pagination
                  current={drawsPage}
                  pageSize={pageSize}
                  total={drawsData.length}
                  onChange={(page) => setDrawsPage(page)}
                  className="pagination"
                />
              </div> */}

              <div
                className="table-footer"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <Pagination
                  current={drawsPage}
                  pageSize={pageSize}
                  total={drawsData.length}
                  onChange={(page) => setDrawsPage(page)}
                  className="pagination"
                />
                <div>
                  <ButtonComponent
                    className="view-btn"
                    text="Download PDF"
                    onClick={handleDownloadDrawsPDF}
                  />
                </div>

              </div>

            </TabPane>

            {/* ✅ Repayments Tab */}
            <TabPane tab="Repayments" key="2">
              {/* Subscription Line of Credit -- one row per Sponser facility */}
              {loanProgrameType === 2 &&
                <Table
                  columns={[
                    { title: "Borrower Fund", dataIndex: "BorrowerFund", render: (value) => value || "-" },
                    { title: "Facility", dataIndex: "Institution", render: (value) => value || "-" },
                    { title: "Total Commitment", dataIndex: "TotalCommitment", render: (value) => formatCurrency(value) },
                    { title: "Unfunded Commitment", dataIndex: "UnfundedCommitment", render: (value) => formatCurrency(value) },
                    { title: "Uncalled Capital", dataIndex: "UncalledCapital", render: (value) => formatCurrency(value) },
                    { title: "Borrowing Base", dataIndex: "BorrowingBase", render: (value) => formatCurrency(value) },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Outstanding </div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => formatCurrency(value),
                    },
                    { title: "Availability", dataIndex: "Availability", render: (value) => formatCurrency(value) },
                    { title: "Advance Rate", dataIndex: "AdvanceRate", render: (value) => formatPercent(value) },
                    { title: "Repayment", dataIndex: "Repayment", render: (value) => formatCurrency(value) },
                    { title: "Post-Repayment Availability", dataIndex: "PostRepaymentAvailability", render: (value) => formatCurrency(value) },
                    { title: "Validation Result", dataIndex: "ValidationResult", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={repaymentsData.slice(
                    (repaymentsPage - 1) * pageSize,
                    repaymentsPage * pageSize
                  )}
                  rowKey="SponserId"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }

              {/* Corporate Loan -- one row per Sponser facility (one portfolio company each) */}
              {loanProgrameType === 4 &&
                <Table
                  columns={[
                    { title: "Portfolio Company", dataIndex: "PortfolioCompany", render: (value) => value || "-" },
                    { title: "Facility", dataIndex: "Institution", render: (value) => value || "-" },
                    { title: "Loan Structure", dataIndex: "LoanStructure", render: (value) => value || "-" },
                    { title: "Original Facility Size", dataIndex: "OriginalFacilitySize", render: (value) => formatCurrency(value) },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Current Outstanding Balance</div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => formatCurrency(value),
                    },
                    { title: "Availability", dataIndex: "Availability", render: (value) => formatCurrency(value) },
                    { title: "Maturity Date", dataIndex: "MaturityDate", render: (value) => formatDate(value) },
                    { title: "Repayment Amount", dataIndex: "RepaymentAmount", render: (value) => formatCurrency(value) },
                    { title: "Post-Repayment Availability", dataIndex: "PostRepaymentAvailability", render: (value) => formatCurrency(value) },
                    { title: "Validation Result", dataIndex: "ValidationResult", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={repaymentsData.slice(
                    (repaymentsPage - 1) * pageSize,
                    repaymentsPage * pageSize
                  )}
                  rowKey="SponserId"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }
              {loanProgrameType !== 2 && loanProgrameType !== 4 &&
                <Table
                  columns={[
                    { title: "Client Name", dataIndex: "BorrowerName" },
                    { title: "Facility Reference", dataIndex: "AccountNumber" },
                    {
                      title: "Loan Amount ",
                      dataIndex: "LoanAmount",
                      render: (value) => value || "-"
                    },
                    {
                      title: (
                        <div style={{ lineHeight: 1.2 }}>
                          <div>Outstanding </div>
                          <div style={{ fontSize: "12px" }}>
                            As of:
                          </div>
                        </div>
                      ),
                      dataIndex: "OutstandingBalanace",
                      render: (value) => value || "-"
                    },
                    { title: "Availability", dataIndex: "Availability" },
                    // Fair Market Value intentionally hidden from the Repayment grid (UI-only) —
                    // still fully present in the API response/state and used internally by
                    // Preview/Save calculations; it's simply not rendered as a column here.
                    // { title: "Fair Market Value", dataIndex: "FairMarketValue" },
                    { title: "Gross Repayment ", dataIndex: "GrossRePayment", render: (value) => value || "-" },
                    { title: "Cost Basis ", dataIndex: "CostBasis" },
                    { title: "Sweep %", dataIndex: "SweepPercentage", render: (value) => value || "-" },
                    { title: "Tax Rate ", dataIndex: "TaxRate" },
                    { title: "Gains ", dataIndex: "Gains" },
                    { title: "Tax", dataIndex: "Tax" },
                    { title: "Repayment Basis Amount", dataIndex: "RepaymentBasisAmount" },
                    {
                      title: "Repayment Amount",
                      dataIndex: "RepaymentAmount",
                    },
                    {
                      title: (
                        <div>
                          Post-Repayment <br />
                          Outstanding
                        </div>
                      ),
                      dataIndex: "PostRepaymentOutstanding",
                    },
                    { title: "Post-Repayment Availability", dataIndex: "PostRepaymentAvailability" },
                    {
                      title: (
                        <div>
                          Post-Repayment <br />
                          LTV
                        </div>
                      ),
                      dataIndex: "PostRepaymentLTV",
                    },
                    { title: "Repayment ", dataIndex: "RePayment" },
                    { title: "Net Repayment ", dataIndex: "NetPayment" },
                    { title: "Fund(s)", dataIndex: "FundPledges", render: (value) => value || "-" },
                    {
                      title: "Action",
                      fixed: "right",
                      render: (_, record) => (
                        <div className="actionColumnDiv">
                          <Checkbox />
                        </div>
                      ),
                    }
                  ]}
                  dataSource={repaymentsData.slice(
                    (repaymentsPage - 1) * pageSize,
                    repaymentsPage * pageSize
                  )}
                  rowKey="BorrowerName"
                  pagination={false}
                  className="transparent-table"
                  locale={{ emptyText: getTableEmptyText() }}
                  scroll={{ x: "max-content" }}
                />
              }
              {/* <div className="table-footer">
                
                <ButtonComponent
                  block
                  className="view-btn"
                  text="Certify & Submit Batch"
                  onClick={handleDownloadRepaymentsPDF}
                />
                <Pagination
                  current={repaymentsPage}
                  pageSize={pageSize}
                  total={repaymentsData.length}
                  onChange={(page) => setRepaymentsPage(page)}
                  className="pagination"
                />
              </div> */}

              <div
                className="table-footer"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <Pagination
                  current={repaymentsPage}
                  pageSize={pageSize}
                  total={repaymentsData.length}
                  onChange={(page) => setRepaymentsPage(page)}
                  className="pagination"
                />
                <div>
                  <ButtonComponent
                    className="view-btn"
                    text="Download PDF"
                    onClick={handleDownloadRepaymentsPDF}
                  />
                </div>

              </div>
            </TabPane>
          </Tabs>
        </Spin>
        {/* )}*/}
      </div>

      <Modal
        title="Certify & Submit Batch"
        visible={certifyModalVisible}
        onCancel={closeCertifyModal}
        footer={null}
        width={600}
        destroyOnClose
      >
        <div style={{ color: "#f4f4f5" }}>
          I, <b>{submittedByName.trim() || "[Name]"}</b>, attest that I am authorized to submit
          this advance/repayment on behalf of the institution. I have reviewed the transaction and
          confirm that the details and calculated amounts are accurate and consistent with the
          approved facility terms. I understand this submission is final, recorded, and subject to
          audit.
        </div>
        <Form form={certifyForm} layout="vertical">
          <Row gutter={16} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12}>
              <Form.Item
                label="Name"
                name="submittedByName"
                rules={[{ required: true, message: "Name is required" }]}
              >
                <Input
                  placeholder="Enter your name"
                  onChange={(e) => setSubmittedByName(e.target.value)}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item label="Date & Time" name="submittedDateDisplay">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
          <Button onClick={closeCertifyModal}>Cancel</Button>
          <Button type="primary" onClick={handleCertifyModalSubmit}>
            Submit
          </Button>
        </div>
      </Modal>

    </SidebarLayout>

  );
};

export default AdvancesAndRepaymentsForBanker;

